/* eslint-disable jsx-a11y/no-static-element-interactions */
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import isEqual from 'react-fast-compare';
import { get, getRulesFromState, applyVerticalStyles } from 'ctm-util-functions';
import { resultsConfigSelector } from 'ctm-selectors';

import { Icon } from '../icon/icon';
import { Text } from '../text/text';
import { SlideDown, FadeIn } from '../animations';
import styles from './accordion.css';

class _Accordion extends Component {

    constructor(props) {
        super(props);

        this.state = {
            isOpen: get(props.stateProps, 'isOpen', false, props.isOpen)
        };

        this.styles = applyVerticalStyles(styles, 'accordion');
    }

    /**
     * Determine if the component should re-render based on the nextProps
     * @param  {Object} nextProps  The incoming set of props
     * @param  {Object} nextState  The incoming state
     * @returns {Boolean} - True if the component should re-render, false otherwise
     */
    shouldComponentUpdate = (nextProps, nextState) => {
        const { isOpen } = this.state;
        // could use a Pure component if we can get rid of the shapped props, cotentProps & stateProps
        return !isEqual(nextProps, this.props) || isOpen !== nextState.isOpen;
    };

    /**
     * Determine whether or not to render an icon
     * @param {String} stateSuffix Empty string or _expanded
     * @returns {Icon|Null} An icon
     */
    getIcon = (stateSuffix) => {
        const { contentProps, icon } = this.props;
        const variant = get(contentProps, 'icon', icon);
        const { iconUIStyles = true } = this.props;
        if (!variant) {
            return null;
        }
        const UIComponent = iconUIStyles ? `icon_feature_1_large_rounded${stateSuffix}` : null;
        return (
            <Icon className={this.styles.icon} variant={variant} UIComponent={UIComponent} size="xlarge" />
        );
    };

    /**
     * Renders a toggle icon up/down arrow
     * @returns {Node} An icon component
     */
    getToggleIcon = () => {
        const { isOpen } = this.state;
        const { cssClass, toggleIconOpen, toggleIconClosed } = this.props;
        const variant = isOpen ? toggleIconOpen : toggleIconClosed;
        const className = this.resolveClassName({ container: cssClass }, 'toggle_icon');
        return (
            <Icon className={className} variant={variant} size="large" />
        );
    };

    /**
     * Renders a text component with the right colour.
     * @returns {Node} A text component
     */
    getTitle = () => {
        const { label, labelSize } = this.props;
        return (
            <Text className={this.styles.text_wrapper}
                label={get(this.props, 'contentProps.label', label)}
                size={labelSize} />
        );
    };

    /**
     * Onclick method to toggle it open or closed.
     */
    toggleAccordion = () => {
        this.setState((prevState) => {
            return { isOpen: !prevState.isOpen };
        });
    };

    /**
     * As the component receives new props we need to check selectedSidebarAccordion value to toggle accordion
     * @param {Object} nextProps - the new props to be received
     */
    componentWillReceiveProps = (nextProps) => {
        const { selectedSidebarAccordion, contentProps, label } = this.props;
        if (nextProps.selectedSidebarAccordion !== selectedSidebarAccordion) {
            this.setState({ isOpen: nextProps.selectedSidebarAccordion === get(contentProps, 'label', label) });
        }
    };

    /**
    * Determine what contents to return
    * @param {String} label label to pass to Text Component
    * @returns {Node|null} Text Component
    */
    getSubLabel = (label) => {
        if (!label) {
            return null;
        }
        return <span className={this.styles.subLabel}>{label}</span>;
    };

    /**
    * Returns children amount
    * @returns {Node|null} Display Amount
    */
    getChildrenAmount = () => {
        const { children, displayChildrenAmount, childrenAmountOverride } = this.props;
        if (!displayChildrenAmount) return null;
        const amount = childrenAmountOverride != null ? childrenAmountOverride : React.Children.count(children);
        return <div className={styles.display_amount}>{amount}</div>;
    };

    /**
    * Determine what contents to return
    * @param {String} stateSuffix stateSuffix to pass to icon
    * @returns {Node} Contents
    */
    getContents = (stateSuffix) => {
        const { subLabel, togglePosition = 'right', additionalHeaderElement } = this.props;
        return (
            <>
                {this.getChildrenAmount()}
                {togglePosition === 'left' ? this.getToggleIcon() : this.getIcon(stateSuffix)}
                {this.getTitle()}
                {this.getSubLabel(subLabel)}
                {togglePosition === 'right' ? this.getToggleIcon() : this.getIcon(stateSuffix)}
                {additionalHeaderElement}
            </>
        );
    };

    /**
    * Determine what className to return
    * @param {String} className className path
    * @returns {Object} className
    */
    resolveClassName = ({ container, suffix = '' }, className) => {
        return this.styles[`${container}_${className}${suffix}`] || this.styles[`${container}_${className}`] || this.styles[className];
    };

    /**
     *
     * @returns {Node} An accordion component
     */
    render = () => {
        const { isOpen } = this.state;
        const { offset = 1000, duration = 225, cssClass, isVisible, openClass, children } = this.props;
        const suffix = isOpen ? '_active' : '';
        const rootClassName = this.styles[cssClass] ? this.styles[cssClass] : cssClass;
        const styleProps = { container: cssClass, suffix };
        const headerClassName = this.resolveClassName(styleProps, 'header');
        const contentClassName = this.resolveClassName(styleProps, 'content');
        return isVisible ? (
            <div className={`${rootClassName} ${isOpen ? openClass : ''}`}>
                <div
                    className={headerClassName}
                    onClick={this.toggleAccordion}
                >
                    {this.getContents(suffix)}
                </div>
                <FadeIn in={isOpen} duration={duration}>
                    <SlideDown in={isOpen} offset={offset} duration={duration}>
                        <div className={contentClassName}>
                            {children}
                        </div>
                    </SlideDown>
                </FadeIn>
            </div>
        ) : null;
    };

}

_Accordion.propTypes = {
    togglePosition: PropTypes.oneOf(['left', 'right']),
    cssClass: PropTypes.string,
    offset: PropTypes.number, // Negative margin used to hide the container by the SlideDown Component
    duration: PropTypes.number,
    subLabel: PropTypes.string,
    displayChildrenAmount: PropTypes.bool,
    childrenAmountOverride: PropTypes.number,
    contentProps: PropTypes.shape({
        icon: PropTypes.string,
        label: PropTypes.string
    }),
    icon: PropTypes.string,
    label: PropTypes.string,
    iconUIStyles: PropTypes.bool,
    stateProps: PropTypes.shape({
        isOpen: PropTypes.bool
    }),
    isOpen: PropTypes.bool,
    children: PropTypes.node,
    selectedSidebarAccordion: PropTypes.string,
    additionalHeaderElement: PropTypes.node,
    isVisible: PropTypes.bool,
    toggleIconOpen: PropTypes.string,
    toggleIconClosed: PropTypes.string,
    openClass: PropTypes.string,
    labelSize: PropTypes.string
};

_Accordion.defaultProps = {
    toggleIconClosed: 'ctm_plus',
    toggleIconOpen: 'ctm_minus',
    openClass: 'expanded',
    labelSize: 'large',
    isVisible: true
};

const mapStateToProps = (state, props) => {
    const selectedSidebarAccordion = resultsConfigSelector(state) ? resultsConfigSelector(state).get('selectedSidebarAccordion') : '';
    const rules = getRulesFromState(state, props);
    const isVisible = get(rules, 'visibility.result', true);
    return {
        selectedSidebarAccordion,
        isVisible
    };
};

const Accordion = connect(mapStateToProps)(_Accordion);

export { Accordion };
export { _Accordion };
